import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';

import Header from './Header';
import Sidebar from './Sidebar';
import BookmarksList from './BookmarksList';
import EditBookmarkDialog from './EditBookmarkDialog';
import ImportDialog from './ImportDialog';
import SettingsDialog from './SettingsDialog';
import CategoryManager from './CategoryManager';
import TagManager from './TagManager';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Dashboard = ({ user, onLogout }) => {
  const [categories, setCategories] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Dialogs
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingBookmark, setEditingBookmark] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);
  const [tagManagerOpen, setTagManagerOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadBookmarks();
  }, [selectedCategory, searchQuery, statusFilter, selectedTag]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadCategories(),
        loadStatuses(),
        loadTags(),
        loadBookmarks()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/categories`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
      toast.error('Failed to load categories');
    }
  };

  const loadTags = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tags`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTags(data);
      }
    } catch (error) {
      console.error('Failed to load tags:', error);
    }
  };

  const loadBookmarks = async () => {
    try {
      let url = `${API_URL}/api/bookmarks?`;
      if (selectedCategory) url += `category_id=${selectedCategory}&`;
      if (statusFilter) url += `status=${statusFilter}&`;
      if (searchQuery) url += `search=${encodeURIComponent(searchQuery)}`;

      const response = await fetch(url, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBookmarks(data);
      }
    } catch (error) {
      console.error('Failed to load bookmarks:', error);
      toast.error('Failed to load bookmarks');
    }
  };

  const loadStatuses = async () => {
    try {
      const response = await fetch(`${API_URL}/api/statuses`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStatuses(data);
      }
    } catch (error) {
      console.error('Failed to load statuses:', error);
    }
  };

  const handleAddBookmark = () => {
    setEditingBookmark(null);
    setEditDialogOpen(true);
  };

  const handleEditBookmark = (bookmark) => {
    setEditingBookmark(bookmark);
    setEditDialogOpen(true);
  };

  const handleDeleteBookmark = async (bookmarkId) => {
    if (!window.confirm('Are you sure you want to delete this bookmark?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/bookmarks/${bookmarkId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Bookmark deleted');
        loadBookmarks();
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      console.error('Failed to delete bookmark:', error);
      toast.error('Failed to delete bookmark');
    }
  };

  const handleBookmarkSaved = () => {
    setEditDialogOpen(false);
    loadBookmarks();
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  const handleCategoryCreated = () => {
    loadCategories();
  };

  const handleImportComplete = () => {
    setImportDialogOpen(false);
    loadData();
  };

  const handleExport = async () => {
    try {
      const response = await fetch(`${API_URL}/api/export`, {
        credentials: 'include'
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tabkeeper_bookmarks_${new Date().toISOString().split('T')[0]}.html`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Bookmarks exported successfully');
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      console.error('Failed to export:', error);
      toast.error('Failed to export bookmarks');
    }
  };

  const handleOpenMultiple = async (bookmarkIds) => {
    const selectedBookmarks = bookmarks.filter(b => bookmarkIds.includes(b.bookmark_id));
    
    if (selectedBookmarks.length === 0) return;

    if (selectedBookmarks.length > 10) {
      if (!window.confirm(`Open ${selectedBookmarks.length} tabs? This might slow down your browser.`)) {
        return;
      }
    }

    // Open tabs sequentially with delay to avoid popup blocker
    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
    
    for (const bookmark of selectedBookmarks) {
      window.open(bookmark.url, '_blank');
      await delay(100);
    }

    toast.success(`Opening ${selectedBookmarks.length} tabs...`);
  };

  return (
    <div className="app-container">
      {/* Mobile Menu Toggle */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="mobile-menu-toggle"
        data-testid="mobile-menu-toggle"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      <Header
        user={user}
        onLogout={onLogout}
        onSearch={setSearchQuery}
        onStatusFilter={setStatusFilter}
        onTagFilter={setSelectedTag}
        onAddBookmark={handleAddBookmark}
        onImport={() => setImportDialogOpen(true)}
        onExport={handleExport}
        onOpenSettings={() => setSettingsDialogOpen(true)}
        onManageCategories={() => setCategoryManagerOpen(true)}
        onManageTags={() => setTagManagerOpen(true)}
        statuses={statuses}
        tags={tags}
        selectedTag={selectedTag}
      />

      <div className="app-content">
        {/* Mobile Sidebar Backdrop */}
        {mobileMenuOpen && (
          <div
            className="sidebar-backdrop md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Desktop Sidebar */}
        <div className="sidebar hidden md:block w-72 lg:w-80">
          <Sidebar
            categories={categories}
            selectedCategory={selectedCategory}
            onCategorySelect={handleCategorySelect}
            onCategoryCreated={handleCategoryCreated}
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          />
        </div>

        {/* Mobile Sidebar */}
        <div className={`sidebar-mobile fixed inset-y-0 left-0 z-50 w-72 bg-background border-r transform transition-transform duration-300 md:hidden ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="flex items-center justify-between p-4 border-b">
            <span className="text-lg font-semibold text-foreground">TabKeeper</span>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 rounded-lg hover:bg-accent/10"
              data-testid="close-mobile-menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <Sidebar
            categories={categories}
            selectedCategory={selectedCategory}
            onCategorySelect={(catId) => {
              handleCategorySelect(catId);
              setMobileMenuOpen(false);
            }}
            onCategoryCreated={handleCategoryCreated}
            collapsed={false}
            onToggleCollapse={() => {}}
          />
        </div>

        <main className="main-content">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
                <p className="mt-4 text-muted-foreground">Loading...</p>
              </div>
            </div>
          ) : (
            <BookmarksList
              bookmarks={bookmarks}
              categories={categories}
              statuses={statuses}
              onEdit={handleEditBookmark}
              onDelete={handleDeleteBookmark}
              onOpenMultiple={handleOpenMultiple}
              selectedCategory={selectedCategory}
            />
          )}
        </main>
      </div>

      {/* Dialogs */}
      {editDialogOpen && (
      <EditBookmarkDialog
        bookmark={editingBookmark}
        categories={categories}
        statuses={statuses}
        tags={tags}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSave={handleBookmarkSaved}
      />
      )}

      {importDialogOpen && (
        <ImportDialog
          onClose={() => setImportDialogOpen(false)}
          onComplete={handleImportComplete}
        />
      )}

      {settingsDialogOpen && (
        <SettingsDialog
          onClose={() => setSettingsDialogOpen(false)}
        />
      )}

      {categoryManagerOpen && (
        <CategoryManager
          categories={categories}
          onCategoryChange={() => {
            loadCategories();
            loadBookmarks();
          }}
          onClose={() => setCategoryManagerOpen(false)}
        />
      )}

      {tagManagerOpen && (
        <TagManager
          onTagChange={() => {
            loadTags();
            loadBookmarks();
          }}
          onClose={() => setTagManagerOpen(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;
